import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Clean and validate API base URL
      let apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      if (!apiBaseUrl) {
        throw new Error('API base URL is not configured');
      }
      // Remove any quotes and trim whitespace
      apiBaseUrl = apiBaseUrl.replace(/^["']|["']$/g, '').trim();
      // Remove trailing slash if present
      apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
      
      const url = `${apiBaseUrl}/api/admin/login`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Required for CORS with credentials
        body: JSON.stringify({ email, password })
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `Login failed with status ${res.status}`);
      }
      
      // Check if response has content before parsing JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Invalid response format. Expected JSON but got: ${contentType || 'empty'}. Response: ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      // Fetch full profile after login
      let fullProfile = data.user;
      try {
        const profileRes = await fetch(`${apiBaseUrl}/api/admin/admins/${data.user._id || data.user.id}`, {
          headers: { 'Authorization': `Bearer ${data.token}` },
        });
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          fullProfile = { ...fullProfile, ...profileData };
        }
      } catch {}
      login(data.token, fullProfile);
      if (fullProfile.role === 'superadmin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/admin/orders');
      }
    } catch (err) {
      // Provide more specific error messages
      if (err.message.includes('JSON')) {
        setError('Server returned an invalid response. Please check your API configuration.');
        console.error('Login error:', err);
        console.error('API Base URL:', import.meta.env.VITE_API_BASE_URL);
      } else {
        setError(err.message || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <SEO
        title="Login"
        description="Access your Elixir account to manage your honey orders, track shipments, and explore exclusive Mediterranean honey varieties."
        keywords="Elixir login, honey account access, Mediterranean honey login, Elixir customer portal"
      />
      <Header />
      <div className="h-[100vh] items-center flex justify-center px-5 lg:px-0">
        <div className="max-w-screen-xl bg-white border shadow sm:rounded-lg flex justify-center flex-1">
          <div className="flex-1 text-center hidden md:flex">
            <div
              className="m-12 xl:m-16 w-full h-[430px] bg-[length:80%] bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/images/login.jpg')`,
              }}
            ></div>
          </div>
          <div className="hidden md:block w-px bg-gray-200 my-12 mx-4" />
          <div className="lg:w-1/2 xl:w-5/12 p-6 sm:p-12">
            <div className=" flex flex-col items-center">
              <div className="text-center">
                <h1 className="text-2xl xl:text-4xl font-extrabold text-[#d4af37]">Login</h1>
                <p className="text-[12px] pt-5 text-gray-500">
                  Hey enter your details to login to your account
                </p>
              </div>
              <div className="w-full flex-1 mt-8">
                <form onSubmit={handleSubmit} className="mx-auto max-w-xs flex flex-col gap-4">
                  <input
                    className="w-full px-5 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                  <input
                    className="w-full px-5 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <button type="submit" disabled={loading} className="mt-5 tracking-wide font-semibold bg-[#d4af37] text-gray-100 w-full py-4 rounded-lg hover:bg-[#d4af37]/80 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none">
                    <svg
                      className="w-6 h-6 -ml-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                    <span className="ml-3">{loading ? 'Logging in...' : 'Login'}</span>
                  </button>
                  {error && <div className="text-red-500 text-sm text-center">{error}</div>}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Login;
